import { EFinancingProgram, EPlanType, EWorksheetDecision } from '@if/core/types'
import { ErrorOutline } from '@mui/icons-material'
import { Grid, Stack, useTheme } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import AvailableCreditTooltip from '@src/components/AvailableCreditTooltip'
import PendingMessage from '@src/components/PendingMessage'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById, useGetMerchantsByIds } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import {
  useComputePersonalLoanFunding,
  useUpdatePersonalLoanWorksheetDecision,
} from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useBusinessErrors, useFinancingConfig } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useConfirmAllIncomesStatus,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
  useMerchantPaymentPlan,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  EPaymentMethodMerchants,
  Merchant,
  NormsMessage,
  PersonalLoanWorksheet,
  PersonalWorksheetDecisionDTO,
  WorksheetDecision,
} from '@src/data/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import translate from '@src/services/translate'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoanSummaryBox from '../components/LoanSummary/LoanSummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import TransactionSection from '../components/Transaction/TransactionSection'
import { useDialogDecisionDefaultValue, useViewTotalObligation } from '../viewWorksheet-hooks'
import { canComputeFunding, getEmptyComputedFundingDto } from '../viewWorksheet-selectors'

const PAY_CUSTOMER_IDS = [EPaymentMethodMerchants.PayCustomerByBankTransfer, EPaymentMethodMerchants.PayCustomerByCheque] as string[]

const PersonalLoanWorksheetViewPage = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { creditApplication, worksheet, isLoading, privateMessages } = useCreditApplicationData()
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined

  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [computeFunding] = useComputePersonalLoanFunding()
  const [updateDecision] = useUpdatePersonalLoanWorksheetDecision()
  const [deleteMessage] = useDeleteMessage()

  const error = useBusinessErrors()
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const merchantPaymentPlan = useMerchantPaymentPlan(creditApplication, personalLoanWorksheet)
  const activePaymentPlan = merchantPaymentPlan ?? merchant?.paymentPlans?.[0]
  const programFinancingConfig = useFinancingConfig(creditApplication?.financingProgramId)
  const [merchantFinancingConfig] = useMerchantFinancingConfig(
    creditApplication?.merchantId,
    creditApplication?.financingProgramId,
  )
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  // Compute merchant IDs to fetch from worksheet payments
  const merchantIdsToFetch = useMemo(() => {
    if (!personalLoanWorksheet?.merchantPayments) return []
    return personalLoanWorksheet.merchantPayments
      .filter((p) => !PAY_CUSTOMER_IDS.includes(p.merchantId))
      .map((p) => p.merchantId)
  }, [personalLoanWorksheet?.merchantPayments])

  const [fetchedMerchants, isLoadingMerchants] = useGetMerchantsByIds(merchantIdsToFetch)

  // Build payment merchants map from fetched data
  const paymentMerchantsByIds = useMemo(() => {
    const map: Record<string, Merchant> = {}
    fetchedMerchants.forEach((m) => {
      map[m.id] = m
    })
    return map
  }, [fetchedMerchants])

  const { id, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, id)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0

  const totalObligation = useViewTotalObligation(personalLoanWorksheet?.amountRequested, computedInfo)

  const user = useUser()

  const worksheetNormWarningMessages = useMemo(() => {
    if (!personalLoanWorksheet?.normsDecision?.warningMessages) return []
    return personalLoanWorksheet.normsDecision.warningMessages.map((wr) => {
      const translatedMessage = translate([`worksheet.norms.${wr.id}`])
      let messageSuffix = ' | '
      wr.parameters.forEach((p, i) => {
        if (i >= 1) messageSuffix = messageSuffix.concat(', ')
        const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
        messageSuffix = messageSuffix.concat(suffix)
      })
      return {
        id: wr.id,
        message: translatedMessage.concat(messageSuffix),
        applicantType: null,
      } as NormsMessage
    })
  }, [personalLoanWorksheet?.normsDecision?.warningMessages])

  const { documents, requiredDocuments } = useCreditApplicationData()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()
  const finalDecisionIsPending = useIsFinalDecisionPending()

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    personalLoanWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.Personal}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'

  useEffect(() => {
    if (creditApplication && personalLoanWorksheet && Object.keys(computedInfo).length === 0) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const hasCoApplicant = creditApplication.coApplicant !== null
      const stateIso = creditApplication?.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication.financingProgramId,
        amountRequested: personalLoanWorksheet.amountRequested ?? 0,
        includeInsurance: personalLoanWorksheet.includeInsurance,
        paymentFrequency: personalLoanWorksheet.paymentFrequency,
        paymentPlanId: personalLoanWorksheet.paymentPlanId,
        term: personalLoanWorksheet.term,
        interestRate,
        hasCoApplicant,
        stateIso: stateIso,
        deliveryOn: personalLoanWorksheet.deliveryOn,
        firstPaymentOn: personalLoanWorksheet.firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: creditApplication.merchantId,
        insuranceCompanyId: personalLoanWorksheet.insuranceCompanyId,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        const fetchFunding = async () => {
          try {
            const data = await computeFunding(computeDto)
            setComputedInfo(data)
          } catch (error) {
            reportErrorToConsole(error)
          }
        }
        void fetchFunding()
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()
        setComputedInfo(emptyComputedDto)
      }
    }
  }, [personalLoanWorksheet, computedInfo, creditApplication, financingConfig.minimumLoanAmount, computeFunding])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (personalLoanWorksheet) {
        const updatedFinalDecision: PersonalWorksheetDecisionDTO = {
          creditApplicationId: personalLoanWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: personalLoanWorksheet.versionTag,
        } as PersonalWorksheetDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        try {
          await updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          })
          closeDialogs()
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    },
    [personalLoanWorksheet, creditApplication, closeDialogs, invalidateFullCreditApplication, updateDecision],
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
  }

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && personalLoanWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField
                value={t(`EPlanType.${planName}`, { ns: 'enum' })}
                label={t('worksheetCommon.program')}
                disabled
              />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <AvailableCreditTooltip availableCreditData={availableCreditData}>
                <div style={{ position: 'relative' }}>
                  <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
                  {creditInProgress !== 0 && (
                    <ErrorOutline
                      sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme.palette.error.main,
                        fontSize: '1.5rem',
                      }}
                    />
                  )}
                </div>
              </AvailableCreditTooltip>

              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}

            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <LoanSummaryBox
                  worksheet={personalLoanWorksheet}
                  computedInfo={computedInfo}
                  merchantPaymentPlan={activePaymentPlan ?? null}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={personalLoanWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>
            {!isLoadingMerchants && (
              <TransactionSection
                worksheet={personalLoanWorksheet}
                creditApplication={creditApplication}
                paymentMerchantsByIds={paymentMerchantsByIds}
                totalObligation={totalObligation}
                merchantPaymentPlan={activePaymentPlan ?? null}
                productsPlans={[]}
              />
            )}
          </Stack>

          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={personalLoanWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default PersonalLoanWorksheetViewPage
